import { Info } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import { useUpdatePackageReq } from '@/api/package-req'
import { usePackageFeesFromRequests } from '@/api/package-req/hooks/usePackageFeesFromRequests'
import { useCreateRPPaymentLink } from '@/api/payments/mutations/useCreateRPPaymentLink'
import { useUserPkgs } from '@/api/user-package/queries/useUserPkgs'
import { AppBreadcrumbs } from '@/components/AppBreadcrumbs'
import { AppTooltip } from '@/components/AppTooltip'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { GrowinWalletSDK } from '@/components/GrowinWalletSDK'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useToast } from '@/hooks/useToast'
import { RoutePath } from '@/RoutePath'

import { uploadFile } from '../../../services/files'
import { PkgPreview } from '../ClientPkg/components/PkgPreview'
import { UploadArea } from '../ReceiptUpload/components/UploadArea'

export default function ReturnPkgPage() {
  const { pkgId } = useParams<{ pkgId: string }>()
  const { data: userPkgs, isLoading } = useUserPkgs()
  const [isUploading, setIsUploading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const { mutateAsync: createRPPaymentLink, isPending: isCreatingPayment } = useCreateRPPaymentLink()
  const { showSuccessToast, showErrorToast } = useToast()
  const pkg = userPkgs?.find(p => p.id === pkgId)
  const { mutateAsync: updatePackageReq } = useUpdatePackageReq()
  const isReturnLabelFalse = pkg?.returnLabelFalse
  const navigate = useNavigate()
  const { data: packageFees } = usePackageFeesFromRequests(pkg?.id)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  useEffect(() => {
    const isSuccess = searchParams.get('callbackStatus') === 'success'
    const isCanceled = searchParams.get('callbackStatus') === 'canceled'
    if (isSuccess) {
      showSuccessToast('החבילה בדרך חזרה!', 'נעדכן אותך ברגע שהבקשה תטופל והחבילה תוחזר לחנות', {
        duration: 5000,
      })
      navigate(RoutePath.ClientPkg)
    } else if (isCanceled) {
      showErrorToast('הייתה שגיאה בבקשה. אנא נסה שנית או פנה לשירות הלקוחות.', 'נעדכן אותך ברגע שהבקשה תטופל והחבילה תוחזר לחנות', {
        duration: 5000,
      })
      navigate(RoutePath.ClientPkg)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const onReturnError = () => {
    setPaymentUrl(null)
    showErrorToast('הייתה שגיאה בבקשה. אנא נסה שנית או פנה לשירות הלקוחות.')
    setPaymentUrl(null)
  }
  // Listen for payment completion messages

  if (!isLoading && !pkg) {
    return <Navigate to={RoutePath.ClientPkg} replace />
  }

  const getTotalPrice = () => {
    const totalPrice =
      (packageFees?.uniteFee ?? 0) +
      (pkg?.delayChargePrice ?? 0) +
      (pkg?.taxFreeFeeUSD ?? 0) +
      (packageFees?.shrinkRequestFeeUSD ?? 0) +
      (packageFees?.photoRequestFeeUSD ?? 0) +
      10 // return package fee
    return totalPrice.toFixed(2)
  }

  const handleSubmit = async () => {
    setIsUploading(true)
    if (!file) return
    try {
      const fileType = file.name.substring(file.name.lastIndexOf('.'), file.name.length)
      // Upload file to S3
      const result = await uploadFile(file, `${pkg!.id}_return_label${fileType}`)

      if (!result.Location) {
        throw new Error('Failed to upload file')
      }

      if (isReturnLabelFalse) {
        await updatePackageReq({
          packageReqId: pkg!.returnRequestId!,
          returnLabel: result.Location,
        })
        showSuccessToast('תווית החזרה עודכנה בהצלחה!')
        navigate(RoutePath.ClientPkg)
      } else {
        handlePayment(result.Location)
      }
    } catch (error) {
      console.error('Error uploading file:', error)

      showErrorToast('הייתה שגיאה בהעלאת הקובץ. אנא נסה שנית.')
    } finally {
      setIsUploading(false)
    }
  }

  const handlePayment = async (returnLabel: string) => {
    if (!pkg) return

    try {
      const paymentFormData = {
        pid: pkg.id,
        returnLabel,
      }
      const paymentLink = await createRPPaymentLink(paymentFormData)

      // Apple wants new page
      setPaymentUrl(paymentLink)
    } catch (error) {
      console.error('Error creating payment link:', error)
      showErrorToast('הייתה שגיאה בבקשה. אנא נסה שנית או פנה לשירות הלקוחות.')
    }
  }

  // Show payment iframe if payment URL is available
  if (paymentUrl) {
    return (
      <div className='mx-auto h-full max-w-xl p-6' dir='rtl'>
        <GrowinWalletSDK authCode={paymentUrl} onError={onReturnError} onClose={() => setPaymentUrl(null)} />
      </div>
    )
  }

  if (!pkg) return <Spinner className='m-auto size-10' />
  // Show upload form
  return (
    <div className='bg-background-app' dir='rtl'>
      {/* Header */}
      <div className='mx-auto max-w-4xl px-4 py-4'>
        <AppBreadcrumbs
          steps={[
            { label: 'החבילות שלי', to: RoutePath.ClientPkg },
            { label: 'החזרת חבילה', to: `${RoutePath.ClientPkgReturn}/${pkgId}` },
          ]}
        />
      </div>
      {/* Main Content */}
      <div className='mx-auto max-w-2xl px-4 py-8'>
        <div className='flex flex-col gap-6'>
          {/* Title Section */}
          <div className='space-y-2 text-center'>
            <h1 className='text-text-primary text-2xl font-medium'>החזרת חבילה לספק</h1>
            <p className='text-text-secondary text-sm leading-relaxed'>
              אנחנו נדאג להחזיר את החבילה לחנות-
              <br />
              פשוט העלה כאן את טופס ההחזרה וכל השאר עלינו!
            </p>
          </div>

          {/* Package Card */}
          <PkgPreview pkg={pkg} currentView='all' hideActions hideStatus />
          <UploadArea onChangeFile={setFile} />

          <AppTooltip
            disableHover
            triggerProps={{ className: 'text-text-link flex items-center gap-2 text-xs self-start' }}
            content='תווית החזרה היא מסמך מהחנות שמאשר שקיבלת את המוצר ושהחזרתך מתקבלת. במידה ויש ברשותך לייבל (מדבקת שילוח) לביצוע ההחזרה אנא צרף אותו. אחרת, אפשר לצרף צילום קבלה, טופס מיוחד מהאתר או כל מסמך אחר שהחנות שלחה לך.'
          >
            <Info className='h-4 w-4' />
            <span>מהי תווית החזרה?</span>
          </AppTooltip>

          {/* Payment Info */}
          {file && !isReturnLabelFalse ? (
            <div className='space-y-4'>
              {/* Summary */}
              <div className='space-y-4'>
                <h3 className='text-text-primary text-sm font-medium'>סיכום כולל</h3>
                <div className='border-border-default rounded-lg border bg-white p-4'>
                  <div className='space-y-3'>
                    <div className='flex justify-between'>
                      <span className='text-text-secondary text-sm'>עמלת החזרת חבילה:</span>
                      <span className='text-text-primary text-sm font-medium'>$10</span>
                    </div>
                    {!!packageFees?.uniteFee && packageFees.uniteFee > 0 && (
                      <div className='flex justify-between'>
                        <span className='text-text-secondary text-sm'>תוספת בגין איחוד חבילות:</span>
                        <span className='text-text-primary text-sm font-medium'>${packageFees.uniteFee}</span>
                      </div>
                    )}
                    {!!pkg?.delayChargePrice && pkg.delayChargePrice > 0 && (
                      <div className='flex justify-between'>
                        <span className='text-text-secondary text-sm'>דמי אחסון:</span>
                        <span className='text-text-primary text-sm font-medium'>${pkg.delayChargePrice}</span>
                      </div>
                    )}
                    {!!pkg?.taxFreeFeeUSD && pkg.taxFreeFeeUSD > 0 && (
                      <div className='flex justify-between'>
                        <span className='text-text-secondary text-sm'>עלות העברה מדלוואר:</span>
                        <span className='text-text-primary text-sm font-medium'>${pkg.taxFreeFeeUSD}</span>
                      </div>
                    )}
                    {!!packageFees?.photoRequestFeeUSD && packageFees.photoRequestFeeUSD > 0 && (
                      <div className='flex justify-between'>
                        <span className='text-text-secondary text-sm'>עמלת צילום חבילה:</span>
                        <span className='text-text-primary text-sm font-medium'>${packageFees.photoRequestFeeUSD}</span>
                      </div>
                    )}
                    {!!packageFees?.shrinkRequestFeeUSD && packageFees.shrinkRequestFeeUSD > 0 && (
                      <div className='flex justify-between'>
                        <span className='text-text-secondary text-sm'>עמלת צמצום חבילה:</span>
                        <span className='text-text-primary text-sm font-medium'>${packageFees.shrinkRequestFeeUSD}</span>
                      </div>
                    )}
                    <div className='border-border-default border-t' />
                    <div className='flex justify-between'>
                      <span className='text-text-primary text-base font-medium'>סה״כ:</span>
                      <span className='text-text-primary text-base font-medium'>${getTotalPrice()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            !isReturnLabelFalse && (
              <div className='bg-button-primary-selected-segment mx-auto mb-4 w-fit rounded-full px-3 py-2'>
                <p className='text-text-secondary text-center text-xs'>השירות כרוך בתשלום מיידי של $10 ולפי תנאי הספק בלבד</p>
              </div>
            )
          )}
          {/* Submit Button */}
          <div className='flex flex-col items-center gap-2'>
            <Button
              onClick={handleSubmit}
              loading={isUploading || isCreatingPayment}
              disabled={!file || isUploading}
              className='w-full max-w-[335px]'
              size='lg'
            >
              {isReturnLabelFalse ? 'העלה תווית החזרה' : 'המשך לתשלום'}
            </Button>
            {!isReturnLabelFalse && (
              <p className='text-text-secondary text-center text-xs'>
                בלחיצה על הכפתור תועבר לעמוד התשלום <br />
                ניתן לבטל בקשת החזרה טרם ביצועה מול שירות הלקוחות
              </p>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog isOpen={!!errorMessage} onClose={() => setErrorMessage('')} cancelText='סגור' title='שים לב!' content={errorMessage} />
    </div>
  )
}
